import React from "react";
import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { CURRENCY } from "../../constants/Variables";
import AppLogo from "../../assets/images/service-provider-logo.png";
import { roundTo2Decimal } from "../../utils";

// Register fonts
Font.register({
  family: "Roboto",
  src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf",
});

Font.register({
  family: "Roboto-Bold",
  src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf",
});

Font.register({
  family: "Roboto-Medium",
  src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf",
});

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 20,
    fontFamily: "Roboto",
  },

  // Header Section
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",

    borderBottom: "2px solid #7922D8",
  },

  invoiceTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#7922D8",
    marginBottom: 4,
  },

  invoiceInfo: {
    fontSize: 10,
    color: "#666666",
    marginBottom: 3,
  },

  invoiceNumber: {
    fontSize: 10,
    color: "#000000",
    fontWeight: "bold",
  },

  dateField: {
    fontSize: 10,
    color: "#000000",
  },

  // Icon placeholder (we'll use text as placeholder)
  iconPlaceholder: {
    width: 80,
    height: 80,
    justifyContent: "center",
    alignItems: "center",
  },

  // Billing Information Section
  billingSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 15,
    marginBottom: 20,
    backgroundColor: "#f9f9f9",
    padding: 15,
    borderRadius: 4,
  },

  billingColumn: {
    width: "48%",
  },

  billingTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#7922D8",
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },

  billingText: {
    fontSize: 10,
    color: "#333333",
    marginBottom: 3,
    lineHeight: 1.4,
  },

  // Itemized List Section
  itemsHeader: {
    backgroundColor: "#7922D8",
    // border: "1px solid #e0e0e0",
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 6,
    marginBottom: 0,
  },

  headerText: {
    fontSize: 10,
    fontWeight: "400",
    color: "#ffffff",
    fontFamily: "Roboto-Medium",
  },

  headerColumn: {
    flexDirection: "column",
    alignItems: "flex-start",
  },

  headerColumnRight: {
    flexDirection: "column",
    alignItems: "flex-end",
  },

  descriptionColumn: {
    width: "52%",
    paddingLeft: 15,
    paddingRight: 8,
  },

  unitCostColumn: {
    width: "16%",
    paddingLeft: 0,
    paddingRight: 10,
  },

  qtyColumn: {
    width: "10%",
    paddingLeft: 0,
    paddingRight: 6,
  },

  amountColumn: {
    width: "22%",
    paddingLeft: 0,
    paddingRight: 15,
  },

  // Item rows
  itemRow: {
    flexDirection: "row",
    paddingVertical: 10,
    paddingHorizontal: 0,
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#f5f5f5",
  },

  itemRowAlternate: {
    backgroundColor: "#ffffff",
  },

  itemText: {
    fontSize: 11,
    color: "#333333",
    lineHeight: 1.5,
  },

  itemSubText: {
    fontSize: 9.5,
    color: "#4a4a4a",
    lineHeight: 1.4,
    marginTop: 3,
  },

  itemMetaText: {
    fontSize: 9.5,
    color: "#444444",
    lineHeight: 1.45,
    marginTop: 2,
    fontFamily: "Roboto-Medium",
  },

  itemTextRight: {
    fontSize: 11,
    color: "#333333",
    lineHeight: 1.5,
    textAlign: "right",
  },

  // Included Products Styles
  includedProductsContainer: {
    paddingLeft: 10,
    paddingTop: 5,
    paddingBottom: 5,
    borderLeft: "2px solid #7922D8",
    marginLeft: 15,
    backgroundColor: "#fafafa",
  },

  includedProductRow: {
    flexDirection: "row",
    paddingVertical: 6,
    paddingHorizontal: 0,
    borderBottom: "1px solid #e8e8e8",
  },

  includedProductLabel: {
    fontSize: 10,
    color: "#666666",
    fontWeight: "bold",
    marginBottom: 4,
    paddingLeft: 0,
  },

  includedProductName: {
    fontSize: 10,
    color: "#555555",
    lineHeight: 1.4,
  },

  includedProductPrice: {
    fontSize: 10,
    color: "#7922D8",
    fontWeight: "bold",
    lineHeight: 1.4,
    textAlign: "right",
  },

  // Summary Section
  summarySection: {
    marginTop: 5,
    alignItems: "flex-end",
    paddingRight: 15,
    // borderTop: "1px solid #dddddd",
    paddingTop: 12,
  },

  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "320px",
    marginBottom: 3,
    paddingVertical: 2.5,
  },

  summaryLabel: {
    fontSize: 11,
    color: "#3f3f3f",
    marginRight: 20,
    fontFamily: "Roboto",
  },

  summaryValue: {
    fontSize: 11,
    color: "#1a1a1a",
    minWidth: 100,
    textAlign: "right",
    fontFamily: "Roboto",
  },

  extraChargesLabel: {
    fontSize: 11,
    color: "#111111",
    fontFamily: "Roboto-Bold",
    fontWeight: "bold",
  },

  totalAmountLabel: {
    fontSize: 11,
    color: "#111111",
    fontFamily: "Roboto-Bold",
    fontWeight: "bold",
    marginRight: 20,
  },

  totalAmountValue: {
    fontSize: 11,
    color: "#111111",
    minWidth: 100,
    textAlign: "right",
    fontFamily: "Roboto-Bold",
    fontWeight: "bold",
  },

  // Total Section
  totalSection: {
    backgroundColor: "#666666",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 15,
    paddingHorizontal: 20,
    marginTop: 10,
    width: "100%",
  },

  totalLabel: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#ffffff",
  },

  totalAmount: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#ffffff",
  },

  // Terms Section
  termsSection: {
    marginTop: 30,
  },

  termsTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#000000",
    marginBottom: 5,
  },

  termsText: {
    fontSize: 12,
    color: "#666666",
  },

  donationSection: {
    marginTop: 15,
    marginBottom: 10,
    padding: 12,
    backgroundColor: "#f3e8ff",
    borderLeft: "3px solid #7922D8",
    borderRadius: 4,
  },

  donationTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#7922D8",
    marginBottom: 4,
    fontFamily: "Roboto-Bold",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },

  donationText: {
    fontSize: 10,
    color: "#333333",
    lineHeight: 1.5,
  },

  // Footer Section
  footer: {
    position: "absolute",
    bottom: 40,
    right: 40,
    flexDirection: "row",
    alignItems: "center",
  },

  footerText: {
    fontSize: 10,
    color: "#666666",
    marginRight: 10,
  },

  footerBrand: {
    fontSize: 10,
    color: "#000000",
    fontWeight: "bold",
  },
});

const EcommerceInvoicePDF = ({
  invoiceData,
  currentLanguage,
  supplierOrders,
  donationDetails,
}) => {
  const { t } = useTranslation(["common"]);
  const {
    subtotal = 0,
    userId,
    address,
    orderId,
    createdAt,
    shippingFee,
    vatAmount,
    platformFee,
    totalBill,
    vat,
    discount,
    order,
  } = invoiceData || {};

  const orderItems = (order?.length ? order : supplierOrders) || [];
  const subtotalWithVat = roundTo2Decimal(subtotal + vatAmount);
  const discountAmount = Number(discount?.amount) || 0;
  const subtotalBeforeDiscount = roundTo2Decimal(subtotal + discountAmount);
  const isSlabDiscount = discount?.type === "slab";
  const isPromoDiscount = discount?.type === "promo";

  const actualAmount = Number(donationDetails?.actualAmount) || 0;
  const showDonation = Boolean(donationDetails) && actualAmount > 0;
  const organizationName =
    donationDetails?.organization?.name?.[currentLanguage] ||
    donationDetails?.organization?.name?.en ||
    donationDetails?.organization?.name?.ro ||
    "-";

  const calculateProductUnitPrice = (price, profitMargin) => {
    const basePrice = Number(price) || 0;
    const margin = Number(profitMargin) || 0;
    const profitAmount = (basePrice * margin) / 100;
    return roundTo2Decimal(basePrice + profitAmount);
  };

  const getVariantLabel = (attributes) => {
    if (!attributes || typeof attributes !== "object") return "";
    const entries = Object.entries(attributes);
    if (entries.length === 0) return "";
    return entries
      .map(
        ([, val]) =>
          val?.label?.[currentLanguage] || val?.label || val?.value || "-",
      )
      .join(", ");
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header Section */}
        <View style={styles.header}>
          <View>
            <Text style={styles.invoiceTitle}>{t("invoice")}</Text>
            <Text style={styles.invoiceInfo}>
              {t("orderId")}:{" "}
              <Text style={styles.invoiceNumber}>{orderId}</Text>
            </Text>
            <Text style={styles.invoiceInfo}>
              {t("invoiceDate")}:{" "}
              <Text style={styles.dateField}>
                {new Date(createdAt).toLocaleDateString()}
              </Text>
            </Text>
          </View>
          <View style={styles.iconPlaceholder}>
            <Image src={AppLogo} alt="logo" width={"100%"} height={"100%"} />
          </View>
        </View>

        {/* Billing Information Section */}
        <View style={styles.billingSection}>
          <View style={styles.billingColumn}>
            <Text style={styles.billingTitle}>{t("billedTo")}</Text>
            <Text
              style={styles.billingText}
            >{`${userId?.firstname} ${userId?.lastname}`}</Text>
            {userId?.signupAs === "business" && (
              <Text style={styles.billingText}>{userId?.cui}</Text>
            )}
            <Text style={styles.billingText}>{address?.address}</Text>
            <Text style={styles.billingText}>{address?.city}</Text>
            <Text style={styles.billingText}>{address?.zipCode}</Text>
          </View>
          <View style={styles.billingColumn}>
            <Text style={styles.billingTitle}>Eaziday</Text>
            <Text style={styles.billingText}>
              Drumul Ciorogârla 297-299 Bucharest Romania
            </Text>
            <Text style={styles.billingText}>RO49754252</Text>
            <Text style={styles.billingText}>+40 735 383 792</Text>
            <Text style={styles.billingText}>support@eaziday.com</Text>
            <Text style={styles.billingText}>https://eaziday.com/</Text>
          </View>
        </View>

        {/* Itemized List Section */}
        <View style={styles.itemsHeader}>
          <View style={[styles.descriptionColumn, styles.headerColumn]}>
            <Text style={styles.headerText}>{t("name")}</Text>
          </View>
          <View style={[styles.qtyColumn, styles.headerColumnRight]}>
            <Text style={styles.headerText}>{t("qty")}</Text>
          </View>
          <View style={[styles.unitCostColumn, styles.headerColumnRight]}>
            <Text style={styles.headerText}>{t("finalPrice")}</Text>
          </View>
          <View style={[styles.amountColumn, styles.headerColumnRight]}>
            <Text style={styles.headerText}>{t("finalPriceXQty")}</Text>
          </View>
        </View>

        {/* Item Rows */}
        {orderItems?.length > 0
          ? orderItems.map((item, index) => {
              const unitPrice = calculateProductUnitPrice(
                item?.pricing?.price,
                item?.pricing?.profitMargin,
              );
              const discountPerUnit = Number(item?.pricing?.discount) || 0;
              const taxableAmount = roundTo2Decimal(
                unitPrice - discountPerUnit,
              );
              const vatPerUnit = Number(item?.pricing?.originalVatAmount) || 0;
              const finalPrice = roundTo2Decimal(taxableAmount + vatPerUnit);
              const qty = Number(item?.selectedQty) || 1;
              const finalPriceXQty = roundTo2Decimal(finalPrice * qty);
              const variantLabel = getVariantLabel(
                item?.selectedVariant?.attributes,
              );

              return (
                <View key={index}>
                  <View
                    style={[
                      styles.itemRow,
                      index % 2 === 1 && styles.itemRowAlternate,
                    ]}
                  >
                    <View style={styles.descriptionColumn}>
                      <Text style={styles.itemText}>
                        {item?.productId?.name?.[currentLanguage] ||
                          "Your Item name"}
                      </Text>
                      {variantLabel ? (
                        <Text style={styles.itemSubText}>{variantLabel}</Text>
                      ) : null}
                      <Text style={styles.itemMetaText}>
                        {t("unitPrice")}: {unitPrice} {CURRENCY} |{" "}
                        {t("discount")}: {discountPerUnit} {CURRENCY}
                      </Text>
                      <Text style={styles.itemMetaText}>
                        {t("priceAfterDiscount")}: {taxableAmount} {CURRENCY} |{" "}
                        {t("vatAmount")}: {vatPerUnit} {CURRENCY}
                      </Text>
                    </View>

                    <View style={styles.qtyColumn}>
                      <Text style={styles.itemTextRight}>{qty}</Text>
                    </View>

                    <View style={styles.unitCostColumn}>
                      <Text style={styles.itemTextRight}>
                        {finalPrice} {CURRENCY}
                      </Text>
                    </View>

                    <View style={styles.amountColumn}>
                      <Text style={styles.itemTextRight}>
                        {finalPriceXQty} {CURRENCY}
                      </Text>
                    </View>
                  </View>
                </View>
              );
            })
          : // Default placeholder rows
            Array.from({ length: 7 }, (_, index) => (
              <View
                key={index}
                style={[
                  styles.itemRow,
                  index % 2 === 1 && styles.itemRowAlternate,
                ]}
              >
                <View style={styles.descriptionColumn}>
                  <Text style={styles.itemText}>Your Item name</Text>
                </View>
                <View style={styles.qtyColumn}>
                  <Text style={styles.itemTextRight}>1</Text>
                </View>
                <View style={styles.unitCostColumn}>
                  <Text style={styles.itemTextRight}>0</Text>
                </View>
                <View style={styles.amountColumn}>
                  <Text style={styles.itemTextRight}>0</Text>
                </View>
              </View>
            ))}

        {/* Summary Section */}
        <View style={styles.summarySection}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>
              {t("subtotal")} {t("excludeVat")}
            </Text>
            <Text style={styles.summaryValue}>
              {subtotalBeforeDiscount} {CURRENCY}
            </Text>
          </View>
          {discountAmount > 0 && isSlabDiscount && (
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>{t("freeCredit")}</Text>
              <Text style={styles.summaryValue}>
                - {discountAmount} {CURRENCY}
              </Text>
            </View>
          )}
          {discountAmount > 0 && isPromoDiscount && (
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>{t("promoDiscount")}</Text>
              <Text style={styles.summaryValue}>
                - {discountAmount} {CURRENCY}
              </Text>
            </View>
          )}
          {discountAmount > 0 && (
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>
                {t("subTotalAfterDiscount")} {t("excludeVat")}
              </Text>
              <Text style={styles.summaryValue}>
                {subtotal} {CURRENCY}
              </Text>
            </View>
          )}
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>
              {t("vatAmount")} ({vat}%)
            </Text>
            <Text style={styles.summaryValue}>
              {vatAmount} {CURRENCY}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>
              {t("subtotal")} {t("includeVat")}
            </Text>
            <Text style={styles.summaryValue}>
              {subtotalWithVat} {CURRENCY}
            </Text>
          </View>

          <View style={styles.summaryRow}>
            <Text style={styles.extraChargesLabel}>{t("extraCharges")}</Text>
          </View>

          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{t("shippingFee")}</Text>
            <Text style={styles.summaryValue}>
              {shippingFee} {CURRENCY}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{t("platformCharges")}</Text>
            <Text style={styles.summaryValue}>
              {platformFee} {CURRENCY}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{t("deliveryType")}</Text>
            <Text style={styles.summaryValue}>
              {invoiceData?.deliveryType || "-"}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{t("paymentType")}</Text>
            <Text style={styles.summaryValue}>
              {invoiceData?.paymentDetail?.paymentMethod || "-"}
            </Text>
          </View>

          <View style={styles.summaryRow}>
            <Text style={styles.totalAmountLabel}>{t("totalAmount")}</Text>
            <Text style={styles.totalAmountValue}>
              {totalBill} {CURRENCY}
            </Text>
          </View>
        </View>

        {showDonation && (
          <View style={styles.donationSection}>
            <Text style={styles.donationTitle}>{t("charitableDonation")}</Text>
            <Text style={styles.donationText}>
              {t("donationInvoiceNotice", {
                amount: roundTo2Decimal(actualAmount),
                currency: CURRENCY,
                percentage: donationDetails?.donationPercentage ?? 0,
                organization: organizationName,
              })}
            </Text>
          </View>
        )}

        {/* Total Section */}
        <View style={styles.totalSection}>
          <Text style={styles.totalLabel}>{t("invoiceTotal")}</Text>
          <Text style={styles.totalAmount}>
            {totalBill} {CURRENCY}
          </Text>
        </View>

        {/* Terms Section */}
        {/* <View style={styles.termsSection}>
          <Text style={styles.termsTitle}>Terms</Text>
          <Text style={styles.termsText}>{terms}</Text>
        </View> */}

        {/* Footer Section */}
        {/* <View style={styles.footer}>
          <Text style={styles.footerText}>
            FreshBooks makes it easier to{" "}
            <Text style={{ textDecoration: "underline" }}>
              get paid faster.
            </Text>
          </Text>
          <Text style={styles.footerBrand}>FreshBooks cloud accounting</Text>
        </View> */}
      </Page>
    </Document>
  );
};

export default EcommerceInvoicePDF;
